import {
  useCreateInvestorProfileDetails,
  useInvestorProfile,
  useUpdateInvestorProfileDetails,
} from "creso-core/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "creso-core/constants";
import {
  IncomeSlab,
  Occupation,
  OtherInfoSchema,
  PepStatus,
  SourceOfWealth,
} from "creso-core/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormSelect,
  useMultiStepForm,
  Button,
  FormToggleGroup,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Client } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";

export function OtherInfo() {
  const { nextStep, previousStep } = useMultiStepForm();
  const { client } = useClientOnboarding();
  const { data: profile } = useInvestorProfile(
    { investment_account_guid: client?.investment_account ?? "" },
    {
      enabled: true,
    }
  );

  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();
  const { mutateAsync: createProfile } = useCreateInvestorProfileDetails();

  const form = useForm<z.infer<typeof OtherInfoSchema>>({
    resolver: zodResolver(OtherInfoSchema),
    values: {
      occupation: profile?.occupation ?? (OCCUPATION[0].value as Occupation),
      income_slab:
        profile?.income_slab ?? (INCOME_RANGE[0].value as IncomeSlab),
      source_of_wealth:
        profile?.source_of_wealth ??
        (SOURCE_OF_WEALTH[0].value as SourceOfWealth),
      pep_details: profile?.pep_details ?? (PEP_STATUS[0].value as PepStatus),
    },
  });

  const { isDirty } = form.formState;

  const onSubmit = async (data: z.infer<typeof OtherInfoSchema>) => {
    try {
      if (profile) {
        await updateProfile({
          ...data,
          investment_account_guid: client?.investment_account ?? "",
          autofilled: profile.autofilled ? !isDirty : false,
        });
      } else {
        await createProfile({
          ...data,
          investment_account_guid: client?.investment_account ?? "",
        });
      }
      nextStep();
    } catch (error) {
      handleError(error, "Failed to update profile details");
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Financial Profile</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="occupation"
            render={({ field }) => (
              <FormSelect label="Occupation" options={OCCUPATION} {...field} />
            )}
          />
          <FormField
            control={form.control}
            name="income_slab"
            render={({ field }) => (
              <FormToggleGroup
                options={INCOME_RANGE}
                {...field}
                label="Income range"
              />
            )}
          />
          <FormField
            control={form.control}
            name="source_of_wealth"
            render={({ field }) => (
              <FormSelect
                label="Source of wealth"
                options={SOURCE_OF_WEALTH}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="pep_details"
            render={({ field }) => (
              <FormToggleGroup
                options={PEP_STATUS}
                {...field}
                label="Political exposure"
              />
            )}
          />
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? profile?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="w-full flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
